import type { ComputedRef, InjectionKey, MaybeRefOrGetter } from 'vue';
import { computed, inject, provide, toValue } from 'vue';

/** One scale for every control: the same name is the same height on a button, an input or a select. */
export type ControlSize = 'sm' | 'md' | 'lg';

/**
 * How wide a control is. The tokens are shared lengths (`--control-width-*`), so fields line up
 * across forms; `auto` is the control's natural width, `full` fills its container.
 */
export type ControlWidth = 'auto' | 'xs' | 'sm' | 'md' | 'lg' | 'full';

/**
 * What a control is given when nobody names a width: free text fills its container, a formatted
 * value (a date, a time) takes the width of its format.
 */
export type ControlWidthKind = 'text' | 'formatted';

/**
 * `legacy` renders a control exactly as it rendered before widths existed, so the old `SphField`
 * and every control placed outside a field keep their look. The new form components opt their
 * subtree into the defaults by providing `legacyWidth: false`; once the old field is gone, the
 * fallback in `useControlWidth` flips and this value goes away.
 */
export type ResolvedControlWidth = ControlWidth | 'legacy';

/** What a container can preset for the controls inside it. A control's own prop always wins. */
export interface ControlDefaults {
    size?: ControlSize
    /** Applies to free-text controls only: a formatted control keeps the width of its format. */
    width?: ControlWidth
    legacyWidth?: boolean
}

const controlDefaultsKey: InjectionKey<ComputedRef<ControlDefaults>> = Symbol('sphControlDefaults');

/** Merges over whatever an outer container provides, so a nested provider only restates what it changes. */
export function provideControlDefaults(defaults: MaybeRefOrGetter<ControlDefaults>): void {
    const parent = inject(controlDefaultsKey, null);

    provide(controlDefaultsKey, computed(() => ({
        ...parent?.value,
        ...stripUndefined(toValue(defaults)),
    })));
}

export function useControlDefaults(): ComputedRef<ControlDefaults> {
    return inject(controlDefaultsKey, computed(() => ({})), false);
}

/** Resolves a control's size: its own prop, then the nearest container's, then the control's fallback. */
export function useControlSize(own: () => ControlSize | undefined, fallback: ControlSize): ComputedRef<ControlSize> {
    const defaults = useControlDefaults();

    return computed(() => own() ?? defaults.value.size ?? fallback);
}

/** Resolves a control's width: its own prop, then the nearest container's, then what the control holds. */
export function useControlWidth(own: () => ControlWidth | undefined, kind: ControlWidthKind): ComputedRef<ResolvedControlWidth> {
    const defaults = useControlDefaults();

    return computed(() => {
        const width = own() ?? (kind === 'text' ? defaults.value.width : undefined);

        if (width) {
            return width;
        }

        if (defaults.value.legacyWidth ?? true) {
            return 'legacy';
        }

        return kind === 'text' ? 'full' : 'auto';
    });
}

/**
 * The classes that give a control's root its width. `max-w-full` keeps a token from pushing a
 * control out of a container narrower than the token. A container sets `--control-width-stretch`
 * to make every control inside it fill, as a stacked form row does on a narrow screen.
 */
export function controlWidthClass(width: ResolvedControlWidth): string {
    return {
        legacy: '',
        auto: 'w-[var(--control-width-stretch,fit-content)] max-w-full',
        xs: 'w-[var(--control-width-stretch,var(--control-width-xs))] max-w-full',
        sm: 'w-[var(--control-width-stretch,var(--control-width-sm))] max-w-full',
        md: 'w-[var(--control-width-stretch,var(--control-width-md))] max-w-full',
        lg: 'w-[var(--control-width-stretch,var(--control-width-lg))] max-w-full',
        full: 'w-full',
    }[width];
}

function stripUndefined<T extends object>(value: T): Partial<T> {
    return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as Partial<T>;
}
